import { ProcessedCell, SortOrder, SortType, TableCellData, TableColumn } from '../types';

const DEFAULT_EMPTY = 'Empty';

export function deriveColumnId(column: TableColumn, index: number): string {
	return column.id ?? `${column.title}-${index}`;
}

function isValidDate(value: unknown): value is Date {
	return value instanceof Date && !Number.isNaN(value.getTime());
}

function resolveSortValue(
	cell: TableCellData | undefined,
	sortType: SortType,
	emptyCellText: string
): { sortValue: string | number | Date | null; isEmpty: boolean; text: string } {
	if (!cell) {
		return { sortValue: null, isEmpty: true, text: emptyCellText };
	}

	const text = cell.text ?? emptyCellText;

	if (cell.sortValue === undefined || cell.sortValue === null || cell.sortValue === '') {
		if (sortType === 'number' || sortType === 'date') {
			return { sortValue: null, isEmpty: true, text: emptyCellText };
		}
		return { sortValue: text, isEmpty: false, text };
	}

	switch (sortType) {
		case 'number': {
			const num = typeof cell.sortValue === 'number' ? cell.sortValue : Number(cell.sortValue);
			if (typeof cell.sortValue === 'string' || Number.isNaN(num)) {
				return { sortValue: null, isEmpty: true, text: emptyCellText };
			}
			return { sortValue: num, isEmpty: false, text };
		}
		case 'date': {
			if (!isValidDate(cell.sortValue)) {
				return { sortValue: null, isEmpty: true, text: emptyCellText };
			}
			return { sortValue: cell.sortValue, isEmpty: false, text };
		}
		default:
			return { sortValue: String(cell.sortValue), isEmpty: false, text };
	}
}

// Builds row-major grid from column config; pads short columns with Empty cells
export function normalizeColumns(
	columns: TableColumn[],
	emptyCellText = DEFAULT_EMPTY
): ProcessedCell[][] {
	const rowCount = Math.max(...columns.map(col => col.cells.length), 0);

	return Array.from({ length: rowCount }, (_, rowIndex) =>
		columns.map(col => {
			const sortType = col.sortType ?? 'text';
			const sortable = col.sortable !== false;
			const cell = col.cells[rowIndex];
			const resolved = resolveSortValue(cell, sortType, emptyCellText);

			return {
				text: resolved.text,
				sortValue: resolved.sortValue,
				isEmpty: resolved.isEmpty,
				ctaLink: cell?.ctaLink,
				onClick: cell?.onClick,
				ctaType: cell?.ctaType,
				isDownload: cell?.isDownload ?? false,
				icon: cell?.icon,
				hideOnMobile: col.hideOnMobile ?? false,
				sortable,
				sortType,
			};
		})
	);
}

export function sortRowIndices(
	rows: ProcessedCell[][],
	colIndex: number,
	sortType: SortType,
	sortOrder: SortOrder
): number[] {
	const items = rows.map((row, index) => ({ index, value: row[colIndex]?.sortValue }));

	items.sort((a, b) => {
		const aEmpty = a.value === null || a.value === '';
		const bEmpty = b.value === null || b.value === '';

		// Empty values sort first; tie-break on original row order.
		if (aEmpty) return bEmpty ? a.index - b.index : -1;
		if (bEmpty) return 1;

		let compare = 0;
		switch (sortType) {
			case 'text':
				compare = String(a.value).localeCompare(String(b.value));
				break;
			case 'number':
				compare = Number(a.value) - Number(b.value);
				break;
			case 'date':
				compare = (a.value as Date).getTime() - (b.value as Date).getTime();
				break;
		}

		if (compare === 0 || Number.isNaN(compare)) {
			compare = a.index - b.index;
		}

		return compare * sortOrder;
	});

	return items.map(({ index }) => index);
}
